import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useSelector } from 'react-redux';
import { HighlightedProductsHeaderConfig } from '../../constants/merchCards/HighlightedProductsHeaderConfig';
import { SCREENS } from '../../constants/tracking/Screens';
import { MERCH_CARD } from '../../constants/tracking/TrackingSources';
import HubProductCTA from '../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useAppSelector } from '../../reducers';
import { isMerchCardCTAsDataLoadingSelector } from '../../selectors/LoadingStateSelectors';
import { productSelector } from '../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import ProductIcon from '../common/ProductIcon';
import DetailedTermSelector from '../common/TermSelectors/DetailedTermSelector';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import IncludedSeatsDisplay from '../merch-card/IncludedSeatsDisplay';
import { StandardFeaturesContainer } from '../merch-card/features-display/features-display-containers/StandardFeaturesContainer';
import { PriceDisplayContainer } from '../merch-card/price-display/PriceDisplayContainer';
import { SubtextDisplayContainer } from '../merch-card/subtext-display/SubtextDisplayContainer';
import { useSkuAdditionalUnits } from '../merch-card/useSkuAdditionalUnits';
import RequiredOnboardingSubtextContainer from '../plan-builder/RequiredOnboardingSubtextContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MobileHighlightedMerchCard = ({
  productLevel,
  productLine,
  gridItemProps
}) => {
  const isAppReady = useGlobalLoading();
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const seleniumId = apiName || 'loading';
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity,
    skuId
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    title = '',
    subtitle = ''
  } = HighlightedProductsHeaderConfig[productLine][productLevel] || {};
  const shouldShowTermSelector = useAppSelector(state => {
    if (!isAppReady || !skuId) return false;
    return shouldShowTermSelectorForMerchSelector(state, {
      skuId
    });
  });
  const skuIdsToUse = isAdditionalUnitSkuIdAQuantityPack && additionalUnitQuantity > 0 && additionalUnitSkuId ? [skuId, additionalUnitSkuId] : [skuId];
  const productConfigurationsToUse = skuIdsToUse.map(currentSkuId => createProductConfig({
    skuId: currentSkuId
  }));
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });

  // In order for the grid to work, we need this to remain enclosed in a Fragment and not a div.
  // Children of the display grid item must get the grid-start attributes directly
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "800",
      position: "relative",
      gridRowStart: 1,
      showBorderTop: true,
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(ProductIcon, {
          iconStyle: {
            marginInlineEnd: '300',
            marginBlockEnd: '200'
          },
          name: productLine,
          tier: productLevel
        }), /*#__PURE__*/_jsx(SectionTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: title
          })
        })]
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "0",
      gridRowStart: 2,
      position: "relative",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitle
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 3,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20],
        widths: [60],
        children: /*#__PURE__*/_jsx(PriceDisplayContainer, {
          skuId: skuId,
          queryParamTerm: queryParamTerm,
          apiName: apiName
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [33],
        widths: [80],
        children: /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
          skuId: skuId
        })
      })
    })), shouldShowTermSelector && /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [41],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(DetailedTermSelector, {
          productConfigurations: productConfigurationsToUse,
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "600",
      gridRowStart: 6,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [34],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          alignItems: "start",
          children: /*#__PURE__*/_jsx(SubtextDisplayContainer, {
            additionalUnitSkuId: additionalUnitSkuId,
            queryParamTerm: queryParamTerm,
            productLine: productLine,
            productLevel: productLevel
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 7,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            screen: SCREENS[productLine],
            trackingSource: MERCH_CARD
          }),
          children: /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
            skuId: skuId,
            trackingSource: MERCH_CARD
          }, ctaVariantProps, {
            children: /*#__PURE__*/_jsx(Flex, {
              flexDirection: "column",
              justifyContent: "center",
              gap: "200",
              inlineSize: "100%",
              children: purchaseMotions.map((purchaseMotion, index) => {
                return /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                    purchaseMotion: purchaseMotion,
                    skuId: skuId,
                    productLine: productLine,
                    additionalUnitSkuId: additionalUnitSkuId,
                    additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
                    trackingSource: MERCH_CARD,
                    use: index === 0 ? 'primary' : 'secondary',
                    purchaseMotionLocation: 'MERCH_CARD'
                  }, ctaVariantProps), purchaseMotion)
                }, purchaseMotion);
              })
            })
          }))
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
      gridTemplateRows: "subgrid",
      gridRowStart: 8,
      gridRowEnd: 10,
      paddingBlockStart: "800",
      children: /*#__PURE__*/_jsx(StandardFeaturesContainer, {
        productLine: productLine,
        productLevel: productLevel,
        apiName: apiName,
        isCollapsible: true
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      gridRowStart: 11,
      paddingBlockStart: "400",
      paddingBlockEnd: "600",
      showBorderBottom: true,
      children: skuId ? /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
          queryParamTerm: queryParamTerm,
          skuIds: [skuId],
          trackingSource: MERCH_CARD,
          includesOnboardingCost: false
        })
      }) : null
    }))]
  });
};
export default MobileHighlightedMerchCard;